import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { X, ChevronLeft, ChevronRight } from 'lucide-react'
import { useLanguage } from '@/contexts/LanguageContext'
import { precacheMedia } from '@/utils/cache'

interface BlogPost {
  id: string
  title: string
  subtitle: string
  date: string
  readTime: string
  category: string
  coverImage: string
  coverVideo?: string
  content?: React.ReactNode
  gallery?: string[]
  videos?: string[]
  contentKey?: string
}

const BlogDetailScreen: React.FC = () => {
  const { slug } = useParams<{ slug: string }>()
  const { t } = useLanguage()

  const blogPosts: Record<string, BlogPost> = {
    'field-work-nacelle-robot': {
      id: 'field-work-nacelle-robot',
      title: t.blog.posts.fieldWorkNacelleRobot.title,
      subtitle: t.blog.posts.fieldWorkNacelleRobot.subtitle,
      date: t.blog.posts.fieldWorkNacelleRobot.date,
      readTime: t.blog.posts.fieldWorkNacelleRobot.readTime,
      category: t.blog.categoryFieldWork,
      coverImage: '/field_robot_above_clouds.jpeg',
      gallery: [
        '/field_robot_nacelle_1.jpeg',
        '/field_robot_nacelle_2.jpeg',
        '/field_operator_nacelle_1.jpeg',
        '/field_robot_above_clouds.jpeg',
        '/robot_inside_blade_main.jpeg',
        '/turbine_inside_1.jpeg'
      ],
      contentKey: 'fieldWorkNacelleRobot'
    },
    'robot-lidar-inspection': {
      id: 'robot-lidar-inspection',
      title: t.blog.posts.robotLidarInspection.title,
      subtitle: t.blog.posts.robotLidarInspection.subtitle,
      date: t.blog.posts.robotLidarInspection.date,
      readTime: t.blog.posts.robotLidarInspection.readTime,
      category: t.blog.categoryTechnology,
      coverImage: '/robot_main_photo.jpeg',
      coverVideo: '/turbine_robot_video_main.mov',
      gallery: [
        '/robot_1.jpeg',
        '/robot_2.jpeg',
        '/robot_3.jpeg',
        '/robot_4.jpeg',
        '/robot_inside_blade.jpeg',
        '/robot_inside_blade_main.jpeg',
        '/robot_inside_blade_main_2.jpeg',
        '/turbine_inside_1.jpeg',
        '/turbine_inside_2.jpeg'
      ],
      videos: [
        '/turbine_robot_inside_video.mp4',
        '/turbine_inside_video_robot.mp4'
      ],
      contentKey: 'robotLidarInspection'
    },
    'drone-robot-inspection': {
      id: 'drone-robot-inspection',
      title: t.blog.posts.droneRobotInspection.title,
      subtitle: t.blog.posts.droneRobotInspection.subtitle,
      date: t.blog.posts.droneRobotInspection.date,
      readTime: t.blog.posts.droneRobotInspection.readTime,
      category: t.blog.categoryInspection,
      coverImage: '/damaged_area_1.jpeg',
      gallery: [
        '/robot_1.jpeg',
        '/robot_2.jpeg',
        '/control_room_1.jpeg',
        '/damaged_area_2.jpeg',
        '/damaged_area_3.jpeg',
        '/damaged_area_4.jpeg',
        '/robot_inside_blade_main.jpeg',
        '/robot_inside_blade_main_2.jpeg'
      ],
      contentKey: 'droneRobotInspection'
    },
    'wind-turbine-technology': {
      id: 'wind-turbine-technology',
      title: t.blog.posts.windTurbineTechnology.title,
      subtitle: t.blog.posts.windTurbineTechnology.subtitle,
      date: t.blog.posts.windTurbineTechnology.date,
      readTime: t.blog.posts.windTurbineTechnology.readTime,
      category: t.blog.categoryEducation,
      coverImage: '/turbines_1.jpeg',
      gallery: [
        '/turbine_upper_1.jpeg',
        '/turbine_upper_2.jpeg',
        '/turbine_blade_1.jpeg',
        '/turbine_blade_2.jpeg',
        '/turbine_inside_1.jpeg',
        '/turbine_inside_2.jpeg'
      ],
      contentKey: 'windTurbineTechnology'
    },
    'worlds-biggest-turbine': {
      id: 'worlds-biggest-turbine',
      title: t.blog.posts.worldsBiggestTurbine.title,
      subtitle: t.blog.posts.worldsBiggestTurbine.subtitle,
      date: t.blog.posts.worldsBiggestTurbine.date,
      readTime: t.blog.posts.worldsBiggestTurbine.readTime,
      category: t.blog.categoryTechnology,
      coverImage: '/world_biggest_turbine_1.jpg',
      gallery: [
        '/world_biggest_turbine_2.jpg',
        '/world_biggest_turbine_3.webp',
        '/world_biggest_turbine_4.png'
      ],
      contentKey: 'worldsBiggestTurbine'
    },
    'rope-access-turbine': {
      id: 'rope-access-turbine',
      title: t.blog.posts.ropeAccessTurbine.title,
      subtitle: t.blog.posts.ropeAccessTurbine.subtitle,
      date: t.blog.posts.ropeAccessTurbine.date,
      readTime: t.blog.posts.ropeAccessTurbine.readTime,
      category: t.blog.categoryInspection,
      coverImage: '/rope_access_turbine_1.jpg',
      gallery: [
        '/rope_access_turbine_2.webp',
        '/rope_access_turbine_3.jpg',
        '/rope_access_turbine_4.jpg'
      ],
      contentKey: 'ropeAccessTurbine'
    },
    'offshore-turbines': {
      id: 'offshore-turbines',
      title: t.blog.posts.offshoreTurbines.title,
      subtitle: t.blog.posts.offshoreTurbines.subtitle,
      date: t.blog.posts.offshoreTurbines.date,
      readTime: t.blog.posts.offshoreTurbines.readTime,
      category: t.blog.categoryEducation,
      coverImage: '/offshore_1.jpg',
      gallery: [
        '/offshore_2.jpg',
        '/offshore_3.jpg',
        '/offshore_4.webp',
        '/offshore_5.webp',
        '/offshore_6.jpg'
      ],
      contentKey: 'offshoreTurbines'
    }
  }

  const post = slug ? blogPosts[slug] : null

  // Lightbox state
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null)
  const [lightboxImages, setLightboxImages] = useState<string[]>([])

  const openLightbox = (images: string[], index: number) => {
    setLightboxImages(images)
    setSelectedImageIndex(index)
  }

  const closeLightbox = () => {
    setSelectedImageIndex(null)
    setLightboxImages([])
  }

  const goToPrevious = () => {
    if (selectedImageIndex !== null && selectedImageIndex > 0) {
      setSelectedImageIndex(selectedImageIndex - 1)
    }
  }

  const goToNext = () => {
    if (selectedImageIndex !== null && selectedImageIndex < lightboxImages.length - 1) {
      setSelectedImageIndex(selectedImageIndex + 1)
    }
  }

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedImageIndex === null) return
      switch (e.key) {
        case 'Escape':
          closeLightbox()
          break
        case 'ArrowLeft':
          goToPrevious()
          break
        case 'ArrowRight':
          goToNext()
          break
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selectedImageIndex, lightboxImages.length])

  // Preload all media for the current blog post
  useEffect(() => {
    if (!post) return
    const mediaToPrecache: string[] = []
    if (post.coverImage) mediaToPrecache.push(post.coverImage)
    if (post.gallery) mediaToPrecache.push(...post.gallery)
    if (post.coverVideo) mediaToPrecache.push(post.coverVideo)
    if (post.videos) mediaToPrecache.push(...post.videos)
    // Preload images via browser Image objects for instant rendering
    mediaToPrecache
      .filter(src => !src.match(/\.(mp4|mov|webm)$/))
      .forEach(src => {
        const img = new Image()
        img.src = src
      })
    // Ensure SW cache has all media including videos
    precacheMedia(mediaToPrecache)
  }, [post?.id])

  const renderBlogContent = (contentKey: string) => {
    const postData = t.blog.posts[contentKey as keyof typeof t.blog.posts]
    if (!postData) return null

    switch (contentKey) {
      case 'fieldWorkNacelleRobot':
        return (
          <>
            <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 mb-4 md:mb-6">
              {postData.heroTitle}
            </h2>

            <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
              {postData.introParagraph1}
            </p>

            <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
              {postData.introParagraph2}
            </p>

            <div className="my-6 md:my-10">
              <div className="grid grid-cols-3 gap-2 md:gap-4 rounded-2xl overflow-hidden">
                <div className="relative aspect-[3/4]">
                  <img src="/field_robot_above_clouds.jpeg" alt={postData.imageCaption1} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <span className="absolute bottom-2 left-2 md:bottom-4 md:left-4 text-white text-xs md:text-sm font-medium">{postData.imageCaption1}</span>
                </div>
                <div className="relative aspect-[3/4]">
                  <img src="/field_robot_nacelle_1.jpeg" alt={postData.imageCaption2} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <span className="absolute bottom-2 left-2 md:bottom-4 md:left-4 text-white text-xs md:text-sm font-medium">{postData.imageCaption2}</span>
                </div>
                <div className="relative aspect-[3/4]">
                  <img src="/field_operator_nacelle_1.jpeg" alt={postData.imageCaption3} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <span className="absolute bottom-2 left-2 md:bottom-4 md:left-4 text-white text-xs md:text-sm font-medium">{postData.imageCaption3}</span>
                </div>
              </div>
            </div>

            <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 mb-3 md:mb-4">
              {postData.deploymentTitle}
            </h3>

            <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
              {postData.deploymentParagraph1}
            </p>

            <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
              {postData.deploymentParagraph2}
            </p>

            <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 mb-3 md:mb-4">
              {postData.resultTitle}
            </h3>

            <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
              {postData.resultParagraph}
            </p>

            <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed">
              {postData.conclusion}
            </p>
          </>
        )

      case 'robotLidarInspection':
        return (
          <>
            <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 mb-4 md:mb-6">
              {postData.heroTitle}
            </h2>

            <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
              {postData.introParagraph1}
            </p>

            <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
              {postData.introParagraph2}
            </p>

            <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 mb-3 md:mb-4">
              {postData.anomalyTitle}
            </h3>

            <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
              {postData.anomalyParagraph1}
            </p>

            <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
              {postData.anomalyParagraph2}
            </p>

            <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 mb-3 md:mb-4">
              {postData.reportTitle}
            </h3>

            <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
              {postData.reportParagraph1}
            </p>

            <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed">
              {postData.conclusion}
            </p>
          </>
        )

      case 'droneRobotInspection':
        return (
          <>
            <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 mb-4 md:mb-6">
              {postData.heroTitle}
            </h2>

            <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
              {postData.introParagraph}
            </p>

            <div className="my-6 md:my-10">
              <div className="grid grid-cols-3 gap-2 md:gap-4 rounded-2xl overflow-hidden">
                <div className="relative aspect-[4/3]">
                  <img src="/robot_inside_blade_main.jpeg" alt={postData.imageCaption1} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <span className="absolute bottom-2 left-2 md:bottom-4 md:left-4 text-white text-xs md:text-sm font-medium">{postData.imageCaption1}</span>
                </div>
                <div className="relative aspect-[4/3]">
                  <img src="/turbines_1.jpeg" alt={postData.imageCaption2} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <span className="absolute bottom-2 left-2 md:bottom-4 md:left-4 text-white text-xs md:text-sm font-medium">{postData.imageCaption2}</span>
                </div>
                <div className="relative aspect-[4/3]">
                  <img src="/robot_operator_1.jpeg" alt={postData.imageCaption3} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <span className="absolute bottom-2 left-2 md:bottom-4 md:left-4 text-white text-xs md:text-sm font-medium">{postData.imageCaption3}</span>
                </div>
              </div>
              <p className="text-center text-sm text-gray-500 mt-3">
                {postData.gridCaption}
              </p>
            </div>

            <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed mb-4 md:mb-8">
              {postData.processParagraph}
            </p>

            <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed">
              {postData.conclusion}
            </p>
          </>
        )

      case 'windTurbineTechnology':
        return (
          <>
            <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 mb-4 md:mb-6">
              {postData.heroTitle}
            </h2>

            <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
              {postData.introParagraph}
            </p>

            <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 mb-3 md:mb-4">
              {postData.workingTitle}
            </h3>

            <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
              {postData.workingParagraph1}
            </p>

            <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
              {postData.workingParagraph2}
            </p>

            <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 mb-3 md:mb-4">
              {postData.turkeyTitle}
            </h3>

            <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
              {postData.turkeyParagraph}
            </p>

            <div className="my-6 md:my-10 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 md:p-8 shadow-lg border border-blue-100">
              <h4 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 mb-4 md:mb-6">
                {postData.regionsTitle}
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
                <div className="flex items-start gap-3 bg-white/60 rounded-lg p-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold mt-0.5">1</span>
                  <div>
                    <p className="font-semibold text-gray-900">{postData.region1Name}</p>
                    <p className="text-sm text-gray-600">{postData.region1Desc}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-white/60 rounded-lg p-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-cyan-500 text-white rounded-full flex items-center justify-center text-sm font-bold mt-0.5">2</span>
                  <div>
                    <p className="font-semibold text-gray-900">{postData.region2Name}</p>
                    <p className="text-sm text-gray-600">{postData.region2Desc}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-white/60 rounded-lg p-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-indigo-500 text-white rounded-full flex items-center justify-center text-sm font-bold mt-0.5">3</span>
                  <div>
                    <p className="font-semibold text-gray-900">{postData.region3Name}</p>
                    <p className="text-sm text-gray-600">{postData.region3Desc}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-white/60 rounded-lg p-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold mt-0.5">4</span>
                  <div>
                    <p className="font-semibold text-gray-900">{postData.region4Name}</p>
                    <p className="text-sm text-gray-600">{postData.region4Desc}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-white/60 rounded-lg p-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-teal-500 text-white rounded-full flex items-center justify-center text-sm font-bold mt-0.5">5</span>
                  <div>
                    <p className="font-semibold text-gray-900">{postData.region5Name}</p>
                    <p className="text-sm text-gray-600">{postData.region5Desc}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-white/60 rounded-lg p-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-emerald-500 text-white rounded-full flex items-center justify-center text-sm font-bold mt-0.5">6</span>
                  <div>
                    <p className="font-semibold text-gray-900">{postData.region6Name}</p>
                    <p className="text-sm text-gray-600">{postData.region6Desc}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-white/60 rounded-lg p-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold mt-0.5">7</span>
                  <div>
                    <p className="font-semibold text-gray-900">{postData.region7Name}</p>
                    <p className="text-sm text-gray-600">{postData.region7Desc}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-white/60 rounded-lg p-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-pink-500 text-white rounded-full flex items-center justify-center text-sm font-bold mt-0.5">8</span>
                  <div>
                    <p className="font-semibold text-gray-900">{postData.region8Name}</p>
                    <p className="text-sm text-gray-600">{postData.region8Desc}</p>
                  </div>
                </div>
              </div>
            </div>

            <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 mb-3 md:mb-4">
              {postData.advantagesTitle}
            </h3>

            <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
              {postData.advantagesParagraph}
            </p>

            <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed">
              {postData.conclusion}
            </p>
          </>
        )

      case 'worldsBiggestTurbine':
        return (
          <>
            <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 mb-4 md:mb-6">
              {postData.heroTitle}
            </h2>

            <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
              {postData.introParagraph}
            </p>

            <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 mb-3 md:mb-4">
              {postData.specTitle}
            </h3>

            <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
              {postData.specParagraph}
            </p>

            <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 mb-3 md:mb-4">
              {postData.powerTitle}
            </h3>

            <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
              {postData.powerParagraph}
            </p>

            <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 mb-3 md:mb-4">
              {postData.futureTitle}
            </h3>

            <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
              {postData.futureParagraph}
            </p>

            <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed">
              {postData.conclusion}
            </p>
          </>
        )

      case 'ropeAccessTurbine':
        return (
          <>
            <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 mb-4 md:mb-6">
              {postData.heroTitle}
            </h2>

            <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
              {postData.introParagraph}
            </p>

            <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 mb-3 md:mb-4">
              {postData.safetyTitle}
            </h3>

            <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
              {postData.safetyParagraph}
            </p>

            <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 mb-3 md:mb-4">
              {postData.advantagesTitle}
            </h3>

            <div className="mb-4 md:mb-6 space-y-3">
              <div className="flex items-start gap-3 bg-blue-50 rounded-lg p-4">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold mt-0.5">1</span>
                <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed">{postData.advantage1}</p>
              </div>
              <div className="flex items-start gap-3 bg-blue-50 rounded-lg p-4">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold mt-0.5">2</span>
                <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed">{postData.advantage2}</p>
              </div>
              <div className="flex items-start gap-3 bg-blue-50 rounded-lg p-4">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold mt-0.5">3</span>
                <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed">{postData.advantage3}</p>
              </div>
              <div className="flex items-start gap-3 bg-blue-50 rounded-lg p-4">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold mt-0.5">4</span>
                <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed">{postData.advantage4}</p>
              </div>
            </div>

            <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed">
              {postData.conclusion}
            </p>
          </>
        )

      case 'offshoreTurbines':
        return (
          <>
            <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 mb-4 md:mb-6">
              {postData.heroTitle}
            </h2>

            <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
              {postData.introParagraph}
            </p>

            <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 mb-3 md:mb-4">
              {postData.advantagesTitle}
            </h3>

            <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
              {postData.advantagesParagraph}
            </p>

            <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 mb-3 md:mb-4">
              {postData.challengesTitle}
            </h3>

            <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
              {postData.challengesParagraph}
            </p>

            <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 mb-3 md:mb-4">
              {postData.turkeyTitle}
            </h3>

            <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
              {postData.turkeyParagraph}
            </p>

            <p className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed">
              {postData.conclusion}
            </p>
          </>
        )

      default:
        return null
    }
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{t.blog.notFound}</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700">
            {t.blog.backToHome}
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section - Similar to RobotScreen */}
      <section className="relative h-[50vh] md:h-[60vh] lg:h-[70vh] overflow-hidden">
        {post.coverVideo ? (
          <video
            src={post.coverVideo}
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <img
            src={post.coverImage}
            alt={post.title}
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-black/40" />
        
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="max-w-4xl"
          >
            <div className="flex items-center gap-4 mb-6">
              <span className="px-4 py-1.5 bg-white/10 backdrop-blur-sm text-white text-sm font-medium rounded-full border border-white/20">
                {post.category}
              </span>
              <span className="text-white/70 text-sm">{post.date}</span>
            </div>
            
            <h1 className="text-2xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight mb-3 md:mb-6 drop-shadow-2xl">
              {post.title}
            </h1>
            <p className="text-base md:text-lg lg:text-xl xl:text-2xl text-white/90 leading-relaxed drop-shadow-lg max-w-3xl">
              {post.subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-8 md:py-12 lg:py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.article
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="prose prose-lg max-w-none"
            >
              {post.contentKey ? renderBlogContent(post.contentKey) : post.content}
            </motion.article>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      {((post.gallery && post.gallery.length > 0) || (post.videos && post.videos.length > 0)) && (
        <section className="py-12 md:py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">
                {t.blog.galleryTitle}
              </h2>
              
              {/* Videos */}
              {post.videos && post.videos.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {post.videos.map((video, index) => (
                    <motion.div
                      key={`video-${index}`}
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className="aspect-video rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow"
                    >
                      <video
                        src={video}
                        controls
                        muted
                        playsInline
                        className="w-full h-full object-cover"
                      />
                    </motion.div>
                  ))}
                </div>
              )}
              
              {/* Images */}
              {post.gallery && post.gallery.length > 0 && (
                <div className="flex flex-wrap justify-center gap-4">
                  {post.gallery.map((image, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className="w-[calc(50%-8px)] md:w-[calc(33.333%-11px)] lg:w-[calc(25%-12px)] aspect-square rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
                      onClick={() => openLightbox(post.gallery!, index)}
                    >
                      <img
                        src={image}
                        alt={`${t.blog.galleryImageAlt} ${index + 1}`}
                        className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                      />
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Image Lightbox */}
      <AnimatePresence>
        {selectedImageIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
            onClick={closeLightbox}
          >
            {/* Close button */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors duration-200 z-10"
            >
              <X className="w-6 h-6 text-white" />
            </button>

            {/* Previous button */}
            {selectedImageIndex > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  goToPrevious()
                }}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors duration-200 z-10"
              >
                <ChevronLeft className="w-8 h-8 text-white" />
              </button>
            )}

            {/* Next button */}
            {selectedImageIndex < lightboxImages.length - 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  goToNext()
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors duration-200 z-10"
              >
                <ChevronRight className="w-8 h-8 text-white" />
              </button>
            )}

            {/* Image */}
            <div className="relative w-full h-full flex items-center justify-center p-4 md:p-8">
              <AnimatePresence mode="wait">
                <motion.img
                  key={selectedImageIndex}
                  src={lightboxImages[selectedImageIndex]}
                  alt={`${t.blog.galleryImageAlt} ${selectedImageIndex + 1}`}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.2 }}
                  className="max-w-full max-h-full object-contain rounded-lg"
                  onClick={(e) => e.stopPropagation()}
                />
              </AnimatePresence>

              {/* Image counter */}
              <div className="absolute bottom-8 left-1/2 -translate-x-1/2 bg-black/70 backdrop-blur-sm px-6 py-3 rounded-full">
                <p className="text-white/60 text-xs text-center">
                  {selectedImageIndex + 1} / {lightboxImages.length}
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  )
}

export default BlogDetailScreen
